import React, { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ActivityDashboardResponse,
  AnalyticsBreakdown,
  AnalyticsGranularity,
} from "../../api/analyticsAdmin";
import {
  BREAKDOWN_TO_DIMENSION,
  formatSessionDuration,
  normalizeSeriesToChartData,
} from "../../helpers/analyticsPlus";
import { formatInteger } from "../../helpers/formatNumber";
import { Loader } from "../Loader";
import { AnalyticsFilters } from "./AnalyticsFilterBar";
import { activityDashboard } from "../../api/analyticsStream";
import StreamProgress from "./StreamProgress";
import { useAnalyticsStream } from "./useAnalyticsStream";
import { GranularityToggle, SERIES_COLORS } from "./activityCharts";
import BreakdownRow from "./BreakdownRow";

// Key of the headline (no-breakdown) series in the dashboard payload.
const HEADLINE_KEY = "none";

// Every breakdown that has a composition, rendered as its own stacked row.
const BREAKDOWN_ROWS: { key: AnalyticsBreakdown; title: string }[] = [
  { key: "Platform", title: "Unique visitors by platform" },
  { key: "Audience", title: "Unique visitors by audience" },
  { key: "AgeBand", title: "Unique visitors by age" },
  { key: "Gender", title: "Unique visitors by gender" },
];

const KpiCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="text-sm text-gray-500">{label}</div>
    <div className="text-2xl font-semibold text-gray-800 mt-1">{value}</div>
  </div>
);

const OverviewTab: React.FC<{ filters: AnalyticsFilters }> = ({ filters }) => {
  const [granularity, setGranularity] = useState<AnalyticsGranularity>("Day");

  // A batch of one. There is no fan-out to spread here — a single request returns
  // the KPIs, the headline trend and every breakdown — but it goes through the
  // stream for the two things that make long ranges survivable at all: the
  // command timeout the endpoint raises, and the heartbeats that stop the router
  // closing the connection while the query runs.
  const sections = useMemo(
    () => ({
      dashboard: activityDashboard({
        from: filters.from,
        to: filters.to,
        platform: filters.platform,
        audience: filters.audience,
        granularity,
      }),
    }),
    [filters, granularity]
  );

  const { state, progress } = useAnalyticsStream(sections);
  const section = state.dashboard;

  // The loader shows on every rebuild, first load or refetch, rather than leaving
  // stale charts under new filters.
  if (!section || section.status === "pending")
    return (
      <>
        <StreamProgress progress={progress} />
        <div className="flex justify-center py-20">
          <Loader className="w-8 h-8" />
        </div>
      </>
    );
  if (section.status === "error")
    return <div className="text-sm text-red-500">{section.message}</div>;

  const data: ActivityDashboardResponse = section.data;

  const chartData = normalizeSeriesToChartData(data.series[HEADLINE_KEY] ?? [], ["all"]);

  const cards = [
    { label: "Unique Visitors", value: formatInteger(data.activeUsers) },
    { label: "Total Sessions", value: formatInteger(data.totalSessions) },
    {
      label: "Avg Session Duration",
      value: formatSessionDuration(data.avgSessionDurationSeconds),
    },
  ];

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((card) => (
          <KpiCard key={card.label} label={card.label} value={card.value} />
        ))}
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-8">
        {/* Granularity is a single shared state mirrored onto every chart's
            top-right toggle (here and in each BreakdownRow). */}
        <div className="flex flex-wrap justify-between gap-4 mb-6">
          <h3 className="text-lg font-medium">Unique visitors</h3>
          <GranularityToggle value={granularity} onChange={setGranularity} />
        </div>

        <div className="h-[360px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="bucketStart" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="all"
                name="Unique visitors"
                stroke={SERIES_COLORS[0]}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="space-y-8">
        {BREAKDOWN_ROWS.map((b) => {
          const dimension = BREAKDOWN_TO_DIMENSION[b.key]!;
          return (
            <BreakdownRow
              key={b.key}
              breakdown={b.key}
              title={b.title}
              series={data.series[dimension] ?? []}
              segmentBuckets={data.segments[dimension] ?? []}
              granularity={granularity}
              onGranularityChange={setGranularity}
            />
          );
        })}
      </div>
    </div>
  );
};

export default OverviewTab;
